import { useEffect, useState } from "react";
import { X } from "lucide-react";

export default function GalleryView({ data, mediaMap = {} }) {
  const layout = data?.layout === "slider" ? "slider" : "grid";
  // mediaMap: { [media_id]: { url, ext, original_name } }  (used in the builder preview)
  // When the gallery comes back from the server via the embed endpoint, items already
  // have `.url` attached. This view handles either shape.
  const items = (data?.items || []).map((it) => ({
    url: it.url || mediaMap[it.media_id]?.url,
    caption: it.caption || "",
    link: it.link || "",
    alt: it.caption || mediaMap[it.media_id]?.original_name || "",
  })).filter((i) => i.url);

  const [open, setOpen] = useState(-1);
  const [slide, setSlide] = useState(0);

  useEffect(() => { setSlide(0); }, [items.length]);

  if (!items.length) {
    return <p className="text-sm text-zinc-400 text-center py-10">No photos yet.</p>;
  }

  if (layout === "slider") {
    const cur = items[slide];
    return (
      <div className="w-full" data-testid="embed-content-wrapper">
        {data?.title ? (
          <h2 className="font-display text-xl font-semibold text-zinc-950 mb-3">{data.title}</h2>
        ) : null}
        <div className="relative rounded-md overflow-hidden bg-zinc-950 aspect-video">
          <img src={cur.url} alt={cur.alt} className="w-full h-full object-contain" />
          {cur.caption ? (
            <div className="absolute inset-x-0 bottom-0 p-3 text-xs text-white bg-gradient-to-t from-black/70 to-transparent">{cur.caption}</div>
          ) : null}
          <button
            type="button" aria-label="Previous"
            onClick={() => setSlide((slide - 1 + items.length) % items.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center bg-white/20 hover:bg-white/40 text-white rounded-full"
          >‹</button>
          <button
            type="button" aria-label="Next"
            onClick={() => setSlide((slide + 1) % items.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center bg-white/20 hover:bg-white/40 text-white rounded-full"
          >›</button>
        </div>
        <div className="flex justify-center gap-1.5 mt-3">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-2 w-2 rounded-full ${i === slide ? "bg-zinc-900" : "bg-zinc-300"}`}
            />
          ))}
        </div>
      </div>
    );
  }

  // Grid layout
  return (
    <div className="w-full" data-testid="embed-content-wrapper">
      {data?.title ? (
        <h2 className="font-display text-xl font-semibold text-zinc-950 mb-3">{data.title}</h2>
      ) : null}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((it, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setOpen(i)}
            className="relative aspect-[4/3] overflow-hidden rounded-md bg-zinc-100 group"
            data-testid={`gallery-tile-${i}`}
          >
            <img src={it.url} alt={it.alt}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
            {it.caption ? (
              <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 text-[11px] text-white bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition">
                {it.caption}
              </div>
            ) : null}
          </button>
        ))}
      </div>

      {open >= 0 ? (
        <div
          className="fixed inset-0 z-[999] bg-black/90 grid place-items-center p-6"
          role="dialog"
          onClick={() => setOpen(-1)}
        >
          <button
            className="absolute top-4 right-5 text-white/90 hover:text-white"
            onClick={() => setOpen(-1)}
            aria-label="Close"
          ><X className="h-6 w-6" /></button>
          <img src={items[open].url} alt={items[open].alt}
            className="max-h-[90vh] max-w-full rounded-md"
            onClick={(e) => e.stopPropagation()} />
          {items[open].caption ? (
            <div className="absolute bottom-6 left-0 right-0 text-center text-white/80 text-sm">
              {items[open].caption}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
